import { CryptoError, sodium, wrapKey, unwrapKey } from './chunk-PXX4LS7M.js';
import { KEY_BYTES, SHARE_LINK_KEY_PARAM, AD, BOX_SECRET_KEY_BYTES, BOX_PUBLIC_KEY_BYTES, MEMBER_WRAP_CONTEXT, SAFETY_CODE_GROUPS, SAFETY_CODE_CONTEXT } from './chunk-SA27ENP5.js';
import { toBase64Url, fromBase64Url, utf8 } from './chunk-LAMR6P2E.js';

// src/crypto/share-link.ts
function buildShareLink(webOrigin, shareId, key) {
  if (key.length !== KEY_BYTES) throw new CryptoError("invalid_key", "share key must be 32 bytes");
  const origin = webOrigin.replace(/\/+$/, "");
  return `${origin}/s/${shareId}#${SHARE_LINK_KEY_PARAM}=${toBase64Url(key)}`;
}
function parseShareLink(url) {
  const u = new URL(url);
  const m = /^\/s\/([^/]+)\/?$/.exec(u.pathname);
  if (!m) throw new CryptoError("malformed", "not a share link");
  const params = new URLSearchParams(u.hash.replace(/^#/, ""));
  const k = params.get(SHARE_LINK_KEY_PARAM);
  if (!k) throw new CryptoError("malformed", "share link has no key");
  let key;
  try {
    key = fromBase64Url(k);
  } catch {
    throw new CryptoError("malformed", "share link key is not base64url");
  }
  if (key.length !== KEY_BYTES) throw new CryptoError("invalid_key", "share key must be 32 bytes");
  return { shareId: decodeURIComponent(m[1]), key };
}

// src/crypto/sharing.ts
function generateBoxKeyPair(seed) {
  const s = sodium();
  const kp = seed ? s.crypto_box_seed_keypair(seed) : s.crypto_box_keypair();
  return { publicKey: kp.publicKey, secretKey: kp.privateKey };
}
var wrapBoxSecretKey = (secretKey, masterKey, userId, options) => wrapKey(masterKey, secretKey, AD.accountBoxKey(userId), options);
function unwrapBoxSecretKey(wrapped, masterKey, userId, publicKey) {
  const secretKey = unwrapKey(masterKey, wrapped, AD.accountBoxKey(userId));
  const derived = sodium().crypto_scalarmult_base(secretKey);
  if (!sodium().memcmp(derived, publicKey)) {
    throw new CryptoError("invalid_key", "secret key does not match the public key");
  }
  return secretKey;
}
function memberWrapKey(mySecretKey, peerPublicKey) {
  if (mySecretKey.length !== BOX_SECRET_KEY_BYTES || peerPublicKey.length !== BOX_PUBLIC_KEY_BYTES)
    throw new CryptoError("invalid_key", "box keys must be 32 bytes");
  const s = sodium();
  let shared;
  try {
    shared = s.crypto_box_beforenm(peerPublicKey, mySecretKey);
  } catch {
    throw new CryptoError("invalid_key", "invalid peer public key");
  }
  return s.crypto_generichash(KEY_BYTES, utf8(MEMBER_WRAP_CONTEXT), shared);
}
function wrapKeyForMember(key, ownerSecretKey, memberPublicKey, ref, options) {
  return wrapKey(memberWrapKey(ownerSecretKey, memberPublicKey), key, memberAd(ref), options);
}
function unwrapKeyFromOwner(wrapped, memberSecretKey, ownerPublicKey, ref) {
  return unwrapKey(memberWrapKey(memberSecretKey, ownerPublicKey), wrapped, memberAd(ref));
}
var memberAd = (r) => AD.memberKey(r.kind, r.targetId, r.version, r.fromUserId, r.toUserId);
var wrapPreviousKey = (newKey, previousKey, kind, targetId, version, options) => wrapKey(newKey, previousKey, AD.previousKey(kind, targetId, version), options);
function unwrapKeyChain(currentKey, currentVersion, chain, kind, targetId) {
  const keys = [currentKey];
  let key = currentKey;
  for (let i = 0; i < chain.length && currentVersion - i > 1; i++) {
    key = unwrapKey(key, chain[i], AD.previousKey(kind, targetId, currentVersion - i));
    keys.push(key);
  }
  return keys;
}
function safetyCode(a, b) {
  const [x, y] = a.userId < b.userId ? [a, b] : [b, a];
  const head = utf8(`${x.userId}|${y.userId}|`);
  const msg = new Uint8Array(head.length + 2 * BOX_PUBLIC_KEY_BYTES);
  msg.set(head, 0);
  msg.set(x.publicKey, head.length);
  msg.set(y.publicKey, head.length + BOX_PUBLIC_KEY_BYTES);
  const h = sodium().crypto_generichash(SAFETY_CODE_GROUPS * 5, msg, utf8(SAFETY_CODE_CONTEXT));
  const groups = [];
  for (let g = 0; g < SAFETY_CODE_GROUPS; g++) {
    let n = 0;
    for (let i = 0; i < 5; i++) n = n * 256 + h[g * 5 + i];
    groups.push(String(n % 1e5).padStart(5, "0"));
  }
  return groups.join(" ");
}

// src/crypto/spaces.ts
var wrapSpaceKey = (masterKey, spaceKey, spaceId, version, options) => wrapKey(masterKey, spaceKey, AD.spaceKey(spaceId, version), options);
var unwrapSpaceKey = (masterKey, wrapped, spaceId, version) => unwrapKey(masterKey, wrapped, AD.spaceKey(spaceId, version));
var wrapNotebookKeyForSpace = (spaceKey, notebookKey, spaceId, notebookId, version, options) => wrapKey(spaceKey, notebookKey, AD.spaceNotebookKey(spaceId, notebookId, version), options);
var unwrapNotebookKeyFromSpace = (spaceKey, wrapped, spaceId, notebookId, version) => unwrapKey(spaceKey, wrapped, AD.spaceNotebookKey(spaceId, notebookId, version));

export { buildShareLink, generateBoxKeyPair, memberWrapKey, parseShareLink, safetyCode, unwrapBoxSecretKey, unwrapKeyChain, unwrapKeyFromOwner, unwrapNotebookKeyFromSpace, unwrapSpaceKey, wrapBoxSecretKey, wrapKeyForMember, wrapNotebookKeyForSpace, wrapPreviousKey, wrapSpaceKey };
//# sourceMappingURL=chunk-3EMOFRLG.js.map
//# sourceMappingURL=chunk-3EMOFRLG.js.map